import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Link from '@mui/material/Link'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'

import {
  filterEmptyMessage,
  matchesInstrumentFilter,
  sortAllocatedInstrumentsByCampaignName,
  sortAllocatedInstrumentsByUpdatedDesc,
  type AllocatedInstrumentFilter,
  type AllocatedInstrumentRow,
} from '../lib/allocated-instruments-display'
import {
  ALLOCATED_INSTRUMENTS_TABLE,
  EDITOR_VIEWER_DASHBOARD_LAYOUT,
} from '../lib/editor-viewer-dashboard-layout'
import { AllocatedInstrumentsStatusFilter } from './AllocatedInstrumentsStatusFilter'
import { AllocatedInstrumentsTable } from './AllocatedInstrumentsTable'

const DEFAULT_ROWS_PER_PAGE = 10
const ROWS_PER_PAGE_OPTIONS = [10, 15, 20] as const

const paginationSx = {
  '& .MuiTablePagination-toolbar': {
    minHeight: EDITOR_VIEWER_DASHBOARD_LAYOUT.paginationMinHeight,
    px: 0,
    justifyContent: 'flex-end',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 14,
    color: '#4a5565',
    letterSpacing: '-0.1504px',
  },
  '& .MuiTablePagination-select': {
    bgcolor: EDITOR_VIEWER_DASHBOARD_LAYOUT.paginationSelectBg,
    borderRadius: '4px',
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: '#4a5565',
  },
} as const

interface Props {
  rows: AllocatedInstrumentRow[]
  canEdit: boolean
}

export function AllocatedInstrumentsSection({ rows, canEdit }: Readonly<Props>) {
  const [statusFilter, setStatusFilter] = useState<AllocatedInstrumentFilter>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [campaignSortDirection, setCampaignSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  )

  const filteredItems = useMemo(() => {
    let list = rows.filter((row) => matchesInstrumentFilter(row, statusFilter))
    if (campaignSortDirection) {
      list = sortAllocatedInstrumentsByCampaignName(list, campaignSortDirection)
    } else {
      list = sortAllocatedInstrumentsByUpdatedDesc(list)
    }
    return list
  }, [rows, statusFilter, campaignSortDirection])

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredItems.slice(start, start + rowsPerPage)
  }, [filteredItems, page, rowsPerPage])

  const handleToggleCampaignSort = () => {
    setCampaignSortDirection((prev) => {
      if (prev === null) return 'asc'
      if (prev === 'asc') return 'desc'
      return null
    })
    setPage(0)
  }

  const isEmpty = rows.length === 0
  const isFilterEmpty = !isEmpty && filteredItems.length === 0

  const renderTableContent = () => {
    if (isEmpty) {
      return (
        <Box
          sx={{ py: 6, textAlign: 'center', px: 3 }}
          data-testid="ev-dashboard-instruments-empty"
        >
          <ScienceOutlinedIcon
            sx={{ fontSize: 40, color: '#94a3b8', mb: 1.5 }}
            aria-hidden
          />
          <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
            No instruments have been allocated to you yet.
          </Typography>
        </Box>
      )
    }

    if (isFilterEmpty) {
      return (
        <Box
          sx={{ py: 6, textAlign: 'center', px: 3 }}
          data-testid="ev-dashboard-instruments-filter-empty"
        >
          <Typography sx={{ fontSize: 14, color: '#6b7280', mb: 1 }}>
            {filterEmptyMessage(statusFilter)}
          </Typography>
          <Link
            component="button"
            type="button"
            data-testid="ev-dashboard-instruments-clear-filter"
            onClick={() => {
              setStatusFilter('all')
              setPage(0)
            }}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear filter
          </Link>
        </Box>
      )
    }

    return (
      <AllocatedInstrumentsTable
        rows={paginatedItems}
        campaignSortDirection={campaignSortDirection}
        onToggleCampaignSort={handleToggleCampaignSort}
        canEdit={canEdit}
      />
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: EDITOR_VIEWER_DASHBOARD_LAYOUT.sectionHeaderGap,
        }}
      >
        <Box
          component="h2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
            maxWidth: '100%',
            m: 0,
            fontWeight: 600,
            fontSize: 18,
            lineHeight: '28px',
            letterSpacing: EDITOR_VIEWER_DASHBOARD_LAYOUT.sectionTitleLetterSpacing,
            color: EDITOR_VIEWER_DASHBOARD_LAYOUT.sectionTitleColor,
          }}
        >
          Allocated Instruments
          <Box
            aria-hidden
            component="span"
            data-testid="sensemaker-ftux-ev-tour1-anchor"
            sx={{
              display: 'inline-block',
              width: 0,
              height: 0,
              flexShrink: 0,
            }}
          />
        </Box>

        <AllocatedInstrumentsStatusFilter
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value)
            setPage(0)
          }}
        />
      </Box>

      <Card
        data-testid="sensemaker-ftux-ev-instrument-list"
        elevation={0}
        sx={{
          bgcolor: ALLOCATED_INSTRUMENTS_TABLE.bodyBg,
          border: `1px solid ${ALLOCATED_INSTRUMENTS_TABLE.cardBorder}`,
          borderRadius: ALLOCATED_INSTRUMENTS_TABLE.cardRadius,
          overflow: 'hidden',
        }}
      >
        {renderTableContent()}
      </Card>

      {!isEmpty && !isFilterEmpty && (
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_e, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]}
          data-testid="ev-dashboard-instruments-pagination"
          sx={paginationSx}
        />
      )}
    </Box>
  )
}
